import upperFirst from 'lodash/upperFirst';
import { useTranslation } from 'next-i18next';
import { useCallback, useMemo } from 'react';
import { CountryCodes, type Market } from '@bridebook/toolbox/src/gazetteer';
import { SearchFacet } from '@bridebook/toolbox/src/seo-facets/get-seo-data';
import { ISearchPageQuery } from '@bridebook/web/app-shared/lib/search/types';
import { formatArea } from '@bridebook/web/lib/search/utils';
import { getFacetFilterInText } from '../quick-filter-texts';

interface IProps {
  prettyCategory: string;
  area: string;
  totalResults?: number;
  fullQuery?: ISearchPageQuery;
  facetsParams?: string[];
  facetsContent?: SearchFacet;
  market: Market;
}

export const useSearchPageTitle = ({
  area,
  facetsContent,
  facetsParams,
  fullQuery,
  market,
  prettyCategory,
  totalResults,
}: IProps) => {
  const { t } = useTranslation('search');

  const getPageTitle = useCallback(() => {
    const page = parseInt(fullQuery?.page?.toString() || '1', 10);
    const facet = facetsParams?.[0];
    const supplierType = facet ? getFacetFilterInText(facet) : '';
    const titlePageNum = page > 1 ? `| ${t('head.pagination.page')} ${page}` : '';

    if (market.country === CountryCodes.FR) {
      return [prettyCategory, formatArea(area, market)].filter(Boolean).join(': ');
    }

    // LIVE-27199 - only UK market titles
    if (market.country === CountryCodes.GB) {
      return [formatArea(area, market), prettyCategory, '- Compare Prices & Reviews']
        .filter(Boolean)
        .join(' ');
    }

    return [
      supplierType,
      prettyCategory,
      t('inArea.inLocationInfo'),
      formatArea(area, market),
      titlePageNum,
    ]
      .filter(Boolean)
      .join(' ');
  }, [area, facetsParams, fullQuery?.page, market, prettyCategory, t]);

  const contentTitle = useMemo(() => {
    if (facetsContent?.fullPageTitleOverride) {
      // Fully override page title https://bridebook.atlassian.net/browse/LIVE-18321
      return facetsContent.fullPageTitleOverride;
    }

    if (facetsContent?.title) {
      return facetsContent.title;
    }
    return getPageTitle();
  }, [facetsContent?.fullPageTitleOverride, facetsContent?.title, getPageTitle]);

  const showCount = totalResults && !facetsContent?.fullPageTitleOverride;
  const count = showCount ? market.formatNumber(totalResults) : '';
  const title = upperFirst(contentTitle || '');

  return [count, title].filter(Boolean).join(' ');
};
